import { Component } from '@theme/component';

/**
 * @typedef {{ image: HTMLImageElement }} Refs
 * @extends {Component<Refs>}
 */
class NaImageLoadingComponent extends Component {
    requiredRefs = ['image'];

    /** @type {IntersectionObserver | null} */
    #observer = null;

    /** @type {AbortController | null} */
    #imageListeners = null;

    /** @type {HTMLImageElement | null} */
    #image = null;

    connectedCallback() {
        super.connectedCallback();
        this.#observeImage();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.#stopObserving();
    }

    updatedCallback() {
        this.#observeImage();
    }

    #observeImage() {
        this.#stopObserving();

        const { image } = this.refs;
        if (image !== this.#image) {
            this.#image = image;
            this.setAttribute('aria-busy', 'true');
            this.removeAttribute('data-loaded');
        }

        if (image.complete) {
            this.#markReady(image);
            return;
        }

        this.#imageListeners = new AbortController();
        const onSettled = () => {
            this.#stopObserving();
            this.#markReady(image);
        };
        image.addEventListener('load', onSettled, { signal: this.#imageListeners.signal });
        image.addEventListener('error', onSettled, { signal: this.#imageListeners.signal });

        this.#observer = new IntersectionObserver((entries) => {
            this.toggleAttribute(
                'data-in-view',
                entries.some((entry) => entry.isIntersecting)
            );
        });
        this.#observer.observe(this);
    }

    #stopObserving() {
        this.#imageListeners?.abort();
        this.#imageListeners = null;
        this.#observer?.disconnect();
        this.#observer = null;
        this.removeAttribute('data-in-view');
    }

    /**
     * @param {HTMLImageElement} image
     */
    async #markReady(image) {
        try {
            await image.decode();
        } catch {
            if (!this.isConnected || image !== this.refs.image) return;
            this.#clearLoadingState();
            return;
        }

        if (!this.isConnected || image !== this.refs.image) return;
        this.#clearLoadingState();
    }

    #clearLoadingState() {
        this.setAttribute('aria-busy', 'false');
        this.setAttribute('data-loaded', '');
    }
}

if (!customElements.get('na-image-loading-component')) {
    customElements.define('na-image-loading-component', NaImageLoadingComponent);
}
